import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import Dropdown from '../../common/Dropdown'
import ToggleSwitch from '../../common/ToggleSwitch'
import TooltipedIcon from '../../common/TooltipedIcon'
import { capitalizeFirstLetter } from '../../../utils/functions'
import '../uploads.scss'
import { InformationIcon } from '../../../icons'

const boolFromAnything = (v) => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return ['true', '1', 'y', 'yes'].includes(v.toLowerCase())
  if (typeof v === 'number') return v === 1
  return false
}

const ViewProductModelsVariants = (props) => {
  const { t } = useTranslation()
  const { modelDetails, handleEditModel, closeModal } = props
  const boolStatuses = ['no', 'yes']

  const headerContent = () => {
    const status = modelDetails?.status
    let statusLabel = ''
    switch (status) {
      case 'active':
        statusLabel = t('active')
        break
      case 'inactive':
        statusLabel = t('inactive')
        break
      case 'obsolete':
        statusLabel = t('obsolete')
        break
      default:
        statusLabel = ''
    }

    return (
      <div className="status-wrapper">
        <div className="label d-flex flex-justify-start flex-align-center">
          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.variant_status_info')}`} orientation="bottom" />
          {t('status')}
        </div>
        <Dropdown
          options={[
            { key: 'active', value: t('active') },
            { key: 'inactive', value: t('inactive') },
            { key: 'obsolete', value: t('obsolete') },
          ]}
          className="dropdown-input"
          isDisabled={true}
          initialSelectedKey={statusLabel}
        />
      </div>
    )
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal variants-wrapper"
        title={t('details')}
        headerContent={headerContent()}
        footerContent={
          <>
            <SimpleButton className="cancel-button" onClick={closeModal}>
              {t('close')}
            </SimpleButton>
            <SimpleButton className="action-button" onClick={handleEditModel}>
              {t('edit')}
            </SimpleButton>
          </>
        }
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('product_models_context.general_model_details').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">{t('model')}</p>
              <div className="value">{modelDetails?.deviceModel?.name?.toUpperCase() || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_type')}</p>
              <div className="value">{modelDetails?.deviceModel?.productType || '-'}</div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('category')}</p>
              <div className="value">{modelDetails?.deviceModel?.category || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('dealer-portal.brand')}</p>
              <div className="value">{modelDetails?.deviceModel?.brand || '-'}</div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.device_function')}</p>
              <div className="value">{modelDetails?.deviceModel?.deviceFunction || '-'}</div>
            </div>
          </div>

          <div className="separator" />
          <div className="section-title">{t('product_models_context.model_variant_details').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('device_context.model_number')}</p>
              <div className="value">{modelDetails?.modelNumber || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_model_name')}</p>
              <div className="value">{modelDetails?.productModelName || '-'}</div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_suffix')}</p>
              <div className="value">
                {capitalizeFirstLetter(modelDetails?.modelCategory || '-')}
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.serial_number_prefix')}</p>
              <div className="value">{modelDetails?.serialNumberPrefix || '-'}</div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.voltage')}</p>
              <div className="value">{modelDetails?.voltage || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.power_cord')}</p>
              <div className="value">{modelDetails?.powerCord || '-'}</div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.mh_available')}</p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.currentStatusMhAvailable)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.currentStatusMhAvailable))])}</span>
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_ce')}</p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.ce)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.ce))])}</span>
              </div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_vci')}</p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.vci)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.vci))])}</span>
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_tuv')}</p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.tuv)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.tuv))])}</span>
              </div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_en13060')}</p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.en13060)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.en13060))])}</span>
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.product_recyclable')}</p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.recyclable)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.recyclable))])}</span>
              </div>
            </div>
          </div>
        </div>
      </PageModal>
    </>
  )
}

export default ViewProductModelsVariants

